import { Button, RichText, SafeImage, Text } from '@/ui';
import { validateImage } from '@lib';
import { IconArrowRight } from '@tabler/icons-react';
import type { APIImageType, AlignmentOptions, WidthOptions } from '@types';
import clsx from 'clsx';

export interface GraphicCTAProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  /** This should be an SVG or PNG in the same color as the theme's foreground */
  image?: APIImageType | Array<APIImageType>;
  imageOpacity?: number;
  height?: 'short' | 'medium' | 'tall';
  blockWidth?: WidthOptions;
  contentAlignment?: AlignmentOptions;
}

export function GraphicCTA({
  image,
  title,
  description,
  imageOpacity = 50,
  buttonText,
  buttonLink,
  height = 'medium',
  className,
  contentAlignment,
  blockWidth,
  ...rest
}: GraphicCTAProps) {
  const { horizontalAlignment, verticalAlignment } = contentAlignment?.alignment || {};

  return (
    <section
      className={clsx('z-0 flex w-full justify-center', {
        'max-w-[theme(screens.3xl)]': blockWidth === 'desktop',
        'max-w-(--breakpoint-2xl)': blockWidth === 'wide',
        'max-w-(--breakpoint-xl)': blockWidth === 'standard',
        'max-w-(--breakpoint-lg)': blockWidth === 'narrow',
        'max-w-(--breakpoint-md)': blockWidth === 'prose',
      })}
    >
      <div
        {...rest}
        className={clsx(
          'relative flex w-full max-w-(--breakpoint-2xl) flex-col overflow-visible px-4 text-center',
          {
            'items-center': horizontalAlignment === 'center' || !horizontalAlignment,
            'items-start': horizontalAlignment === 'left',
            'items-end': horizontalAlignment === 'right',
          },
          {
            'justify-center': verticalAlignment === 'middle' || !verticalAlignment,
            'justify-start': verticalAlignment === 'top',
            'justify-end': verticalAlignment === 'bottom',
          },

          {
            'py-16': height === 'medium',
            'py-8': height === 'short',
            'py-24': height === 'tall',
          },
          {
            'gap-4': height === 'short' || height === 'medium',
            'gap-8': height === 'tall',
          },
          className,
        )}
      >
        {validateImage(image) && (
          <SafeImage
            src={image}
            metaProps={{ alt: title }}
            width={0}
            height={0}
            style={{ opacity: `${imageOpacity}%` }}
            sizes={'150%'}
            className={'pointer-events-none absolute left-0 z-0 h-[150%] w-auto -translate-x-1/2 object-contain'}
          />
        )}

        <Text element={'h2'} className={'max-w-3xl border-none p-0'}>
          {title}
        </Text>

        {description && <RichText className={'mt-2 max-w-3xl text-base sm:text-lg'}>{description}</RichText>}

        {buttonText && buttonLink && (
          <Button variant={'ghost'} size={'lg'} className={'gap-4 text-base sm:text-lg'} href={buttonLink}>
            {buttonText}
            <IconArrowRight />
          </Button>
        )}
      </div>
    </section>
  );
}
